import React, { useEffect, useState, useRef } from "react";
import { api } from "../lib/api";
import { Link } from "react-router-dom";
import { Briefcase, Lightbulb, Users, Building2, History, Image, FileText, BarChart3, Settings, Download, Upload, Sparkles, Palette, LineChart } from "lucide-react";
import { toast } from "sonner";

const CARDS = [
  { key: "cases", label: "Cases", to: "/admin/cases", icon: Briefcase, color: "bg-accent-orange text-black" },
  { key: "insights", label: "Insights", to: "/admin/insights", icon: Lightbulb, color: "bg-accent-yellow text-black" },
  { key: "services", label: "Serviços", to: "/admin/services", icon: FileText, color: "bg-accent-green text-black" },
  { key: "team", label: "Equipe", to: "/admin/team", icon: Users, color: "bg-black text-white" },
  { key: "clients", label: "Clientes", to: "/admin/clients", icon: Building2, color: "bg-neutral-200 text-black" },
  { key: "history", label: "Histórico", to: "/admin/history", icon: History, color: "bg-neutral-800 text-white" },
];

export default function Dashboard() {
  const [counts, setCounts] = useState({});
  const [seeding, setSeeding] = useState(false);
  const importInputRef = useRef(null);

  useEffect(() => {
    (async () => {
      const out = {};
      for (const c of CARDS) {
        try {
          const { data } = await api.get(`/content/${c.key}`);
          out[c.key] = data.length;
        } catch { out[c.key] = "—"; }
      }
      setCounts(out);
    })();
  }, []);

  const runSeed = async () => {
    setSeeding(true);
    try {
      // Lazily import current static data
      const [{ SITE, I18N }, { SERVICES, MARQUEE_SERVICES, PILLARS, DEPARTMENTS, OPTIONS }, { CASES, CASE_CATEGORIES }, { INSIGHTS, INSIGHT_TOPICS }, { TEAM, REASONS, FOUNDERS }, { CLIENTS, CLIENT_CATEGORIES }, { HISTORY }, { STATS, TIMELINE }] = await Promise.all([
        import("@/data/site"), import("@/data/services"), import("@/data/cases"), import("@/data/insights"), import("@/data/team"), import("@/data/clients"), import("@/data/history"), import("@/data/stats"),
      ]);
      const payload = {
        site: SITE, i18n: I18N, stats: STATS, timeline: TIMELINE, pillars: PILLARS, reasons: REASONS,
        marquee: MARQUEE_SERVICES, case_categories: CASE_CATEGORIES, insight_topics: INSIGHT_TOPICS,
        departments: DEPARTMENTS, service_options: OPTIONS, client_categories: CLIENT_CATEGORIES,
        home_sections: { founders: FOUNDERS },
        cases: CASES, insights: INSIGHTS, services: SERVICES, team: TEAM, clients: CLIENTS, history: HISTORY,
      };
      const { data } = await api.post("/admin/seed", payload);
      toast.success(`Seed concluído: ${Object.keys(data).length} chaves.`);
      // refresh counts
      setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      toast.error("Erro ao popular conteúdo.");
    } finally { setSeeding(false); }
  };

  const doExport = async () => {
    try {
      const { data } = await api.get("/admin/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `places-cms-backup-${new Date().toISOString().slice(0,10)}.json`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Backup baixado.");
    } catch { toast.error("Erro no export."); }
  };

  const doImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!window.confirm("Atenção: o import substitui o conteúdo atual (uma versão de backup é gerada antes). Continuar?")) {
      e.target.value = ""; return;
    }
    try {
      const text = await file.text();
      const payload = JSON.parse(text);
      const { data } = await api.post("/admin/import", payload);
      toast.success(`Import concluído: ${data.counts.kv} chaves, ${Object.keys(data.counts.collections).length} coleções.`);
      setTimeout(() => window.location.reload(), 800);
    } catch (err) {
      toast.error("Falha no import: arquivo inválido?");
    } finally { e.target.value = ""; }
  };

  return (
    <div>
      <div className="flex justify-between items-end mb-8 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-4xl">Painel.</h1>
          <p className="text-sm text-neutral-500 mt-2">Gerencie todo o conteúdo do site Places em tempo real.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={doExport} data-testid="export-btn" className="text-xs px-3 py-2 border border-black/15 rounded-full hover:bg-black/5"><Download className="w-3 h-3 inline mr-1" /> Exportar JSON</button>
          <button onClick={() => importInputRef.current?.click()} data-testid="import-btn" className="text-xs px-3 py-2 border border-black/15 rounded-full hover:bg-black/5"><Upload className="w-3 h-3 inline mr-1" /> Importar JSON</button>
          <input ref={importInputRef} type="file" accept="application/json" onChange={doImport} className="hidden" data-testid="import-input" />
          <button onClick={runSeed} disabled={seeding} data-testid="seed-btn" className="bg-black text-white text-sm px-4 py-2 rounded-full hover:bg-accent-orange transition disabled:opacity-50">{seeding ? "Populando…" : "Importar conteúdo inicial"}</button>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {CARDS.map((c) => (
          <Link key={c.key} to={c.to} data-testid={`dash-${c.key}`} className={`block p-6 rounded-lg ${c.color} hover:scale-[1.02] transition`}>
            <c.icon className="w-6 h-6 mb-3" />
            <div className="font-display text-3xl">{counts[c.key] ?? "—"}</div>
            <div className="text-sm font-medium mt-1">{c.label}</div>
          </Link>
        ))}
      </div>
      <div className="mt-12 grid md:grid-cols-3 gap-4">
        <Link to="/admin/concierge" className="block p-5 border border-accent-orange/30 bg-accent-orange/5 rounded-lg hover:bg-accent-orange/10 transition" data-testid="dash-concierge"><Sparkles className="w-5 h-5 mb-2 text-accent-orange" /><div className="font-semibold">Concierge — Leads</div><div className="text-xs text-neutral-500 mt-1">Conversas e contatos capturados pelo Concierge IA.</div></Link>
        <Link to="/admin/branding" className="block p-5 border border-black/10 rounded-lg hover:bg-black/5 transition" data-testid="dash-branding"><Palette className="w-5 h-5 mb-2" /><div className="font-semibold">Marca & Visual</div><div className="text-xs text-neutral-500 mt-1">Logo, cores, velocidade do marquee.</div></Link>
        <Link to="/admin/analytics" className="block p-5 border border-black/10 rounded-lg hover:bg-black/5 transition" data-testid="dash-analytics"><LineChart className="w-5 h-5 mb-2" /><div className="font-semibold">Analytics & Pixels</div><div className="text-xs text-neutral-500 mt-1">GA4, GTM, Meta Pixel, Hotjar, custom.</div></Link>
        <Link to="/admin/site" className="block p-5 border border-black/10 rounded-lg hover:bg-black/5 transition"><Settings className="w-5 h-5 mb-2" /><div className="font-semibold">Configurações gerais</div><div className="text-xs text-neutral-500 mt-1">Marca, contatos, unidades, redes sociais.</div></Link>
        <Link to="/admin/i18n" className="block p-5 border border-black/10 rounded-lg hover:bg-black/5 transition"><BarChart3 className="w-5 h-5 mb-2" /><div className="font-semibold">Traduções PT / EN / ES</div><div className="text-xs text-neutral-500 mt-1">Edite todos os textos das 3 línguas.</div></Link>
        <Link to="/admin/media" className="block p-5 border border-black/10 rounded-lg hover:bg-black/5 transition"><Image className="w-5 h-5 mb-2" /><div className="font-semibold">Biblioteca de mídia</div><div className="text-xs text-neutral-500 mt-1">Upload e gestão de imagens.</div></Link>
      </div>
    </div>
  );
}
